import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { ArrowLeft, RefreshCcw, LogOut, BarChart3, Users, MapPin, FileSpreadsheet } from "lucide-react";
import { fetchStats, api, adminLogout } from "@/lib/api";
import { logger } from "@/lib/logger";
import GeoHeatmap from "@/components/admin/GeoHeatmap";
import LeadPipelineKanban from "@/components/admin/LeadPipelineKanban";
import WaitlistExport from "@/components/admin/WaitlistExport";

const COLORS = ["#3B4231", "#6F7F61", "#C8D4BC", "#B8A46A", "#1F2420", "#8A8F87"];

function MetricCard({ label, value, sub, testid }) {
  return (
    <div className="card-standard" data-testid={testid} style={{ padding: 24 }}>
      <div className="form-label" style={{ color: "var(--color-accent)" }}>{label}</div>
      <div className="font-display text-[42px] text-primary leading-none mt-1">{value}</div>
      {sub && <div className="body-sm mt-2">{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // Block search engines from indexing the admin dashboard
  useEffect(() => {
    const originalTitle = document.title;
    document.title = "Admin Dashboard · Feaststar";
    // Modify existing meta[name=robots] if present, else create a new one.
    let meta = document.querySelector('meta[name="robots"]');
    const created = !meta;
    let original = "";
    if (meta) {
      original = meta.getAttribute("content") || "";
    } else {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex, nofollow, noarchive, nosnippet");
    return () => {
      document.title = originalTitle;
      if (created) {
        if (meta.parentNode) meta.parentNode.removeChild(meta);
      } else {
        meta.setAttribute("content", original);
      }
    };
  }, []);

  // Auth gate: verify HttpOnly session cookie via /admin/me on mount.
  // The token is no longer readable from JavaScript — the browser attaches
  // the cookie automatically on api.get() thanks to withCredentials.
  useEffect(() => {
    api.get("/admin/me")
      .then(() => setAuthed(true))
      .catch((err) => {
        logger.warn("[admin] session verify failed:", err);
        navigate("/admin/login", { replace: true });
      });
  }, [navigate]);

  const logout = async () => {
    try {
      await adminLogout();
    } finally {
      // Always redirect, even if the network call failed.
      navigate("/admin/login", { replace: true });
    }
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["stats"],
    queryFn: () => fetchStats(30),
    refetchInterval: 30_000,
    enabled: authed,
    retry: false,
  });

  if (!authed) {
    return <div className="admin-login-wrap" data-testid="admin-checking" />;
  }

  const stats = data || {
    total: 0, by_page: {}, by_device: {}, by_utm: {}, daily_series: [], recent: [],
    partner_leads_total: 0, waitlist_total: 0, restaurant_origins_total: 0,
  };
  const homeCount = stats.by_page?.home || 0;
  const partnerCount = stats.by_page?.partner || 0;
  const customerCount = stats.by_page?.customer || 0;

  const deviceData = Object.entries(stats.by_device || {}).map(([k, v]) => ({ name: k, value: v }));
  const utmData = Object.entries(stats.by_utm || {}).map(([k, v]) => ({ name: k, value: v }));
  const pageData = [
    { name: "Home", value: homeCount },
    { name: "Partner", value: partnerCount },
    { name: "Customer", value: customerCount },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-bg">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-12 py-10">
        <header className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div className="min-w-0">
            <Link to="/" className="body-sm hover:text-primary inline-flex items-center gap-1 mb-3" data-testid="admin-back">
              <ArrowLeft size={12} /> Back
            </Link>
            <h1 className="display-md text-primary">Feaststar Admin</h1>
            <p className="body-md mt-1">Tracking · Leads · Geo · Export</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                refetch();
                qc.invalidateQueries({ queryKey: ["geo-stats"] });
                qc.invalidateQueries({ queryKey: ["partner-leads"] });
              }}
              data-testid="admin-refresh"
              className="btn-primary text-sm"
              aria-label="Daten aktualisieren"
            >
              <RefreshCcw size={14} /> <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="btn-ghost text-sm"
              aria-label="Abmelden"
            >
              <LogOut size={14} /> <span className="hidden sm:inline">Abmelden</span>
            </button>
          </div>
        </header>

        {/* Tab navigation */}
        <nav className="flex gap-1 mb-8 border-b border-soft overflow-x-auto" role="tablist" data-testid="admin-tabs">
          {[
            { id: "overview", label: "Übersicht", icon: BarChart3 },
            { id: "leads", label: "Lead-Pipeline", icon: Users },
            { id: "geo", label: "Geo-Heatmap", icon: MapPin },
            { id: "export", label: "Waitlist-Export", icon: FileSpreadsheet },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-tab-${tab.id}`}
                className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors whitespace-nowrap ${
                  active ? "text-primary" : "text-muted hover:text-primary"
                }`}
              >
                <Icon size={14} />
                {tab.label}
                {active && (
                  <span
                    className="absolute left-0 right-0 -bottom-px h-0.5"
                    style={{ background: "var(--color-primary)" }}
                  />
                )}
              </button>
            );
          })}
        </nav>

        {/* TAB: OVERVIEW (original metrics + charts) */}
        {activeTab === "overview" && (
          <div data-testid="admin-tab-content-overview">
            {/* Metrics row */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              <MetricCard label="Scans gesamt" value={stats.total} sub="30 Tage" testid="metric-total" />
              <MetricCard label="Home" value={homeCount} sub="/" testid="metric-home" />
              <MetricCard label="Partner" value={partnerCount} sub="/partner" testid="metric-partner" />
              <MetricCard label="Geräte" value={Object.keys(stats.by_device || {}).length} sub={Object.keys(stats.by_device || {}).join(" · ") || "—"} testid="metric-devices" />
            </div>

            <div className="grid sm:grid-cols-3 gap-5 mb-10">
              <MetricCard label="Partner Leads" value={stats.partner_leads_total} sub="Total" testid="metric-partner-leads" />
              <MetricCard label="Waitlist Signups" value={stats.waitlist_total} sub="Total" testid="metric-waitlist" />
              <MetricCard label="Restaurant-Origins" value={stats.restaurant_origins_total} sub="QR Auswahl" testid="metric-origins" />
            </div>

            {/* Charts */}
            <div className="grid lg:grid-cols-3 gap-5 mb-10">
              <div className="card-standard lg:col-span-2 min-w-0" data-testid="chart-daily">
                <div className="form-label" style={{ color: "var(--color-accent)" }}>Tägliche Scans</div>
                <div className="h-64 mt-3 min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.daily_series}>
                      <CartesianGrid stroke="#D4D7D0" strokeDasharray="2 2" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#8A8F87" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#8A8F87" width={32} />
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D4D7D0", fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="home" stroke="#1F2420" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="partner" stroke="#6F7F61" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="customer" stroke="#B8A46A" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="card-standard min-w-0" data-testid="chart-devices">
                <div className="form-label" style={{ color: "var(--color-accent)" }}>Geräte</div>
                <div className="h-64 mt-3 min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={deviceData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                        {deviceData.map((entry, idx) => (
                          <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D4D7D0", fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-5 mb-10">
              <div className="card-standard min-w-0" data-testid="chart-page">
                <div className="form-label" style={{ color: "var(--color-accent)" }}>Pages</div>
                <div className="h-56 mt-3 min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={pageData}>
                      <CartesianGrid stroke="#D4D7D0" strokeDasharray="2 2" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#8A8F87" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#8A8F87" width={32} />
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D4D7D0", fontSize: 12 }} />
                      <Bar dataKey="value" fill="#1F2420" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="card-standard min-w-0" data-testid="chart-utm">
                <div className="form-label" style={{ color: "var(--color-accent)" }}>Quellen</div>
                <div className="h-56 mt-3 min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={utmData} layout="vertical">
                      <CartesianGrid stroke="#D4D7D0" strokeDasharray="2 2" />
                      <XAxis type="number" tick={{ fontSize: 11 }} stroke="#8A8F87" />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke="#8A8F87" width={80} />
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D4D7D0", fontSize: 12 }} />
                      <Bar dataKey="value" fill="#6F7F61" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Recent table */}
            <div className="card-standard overflow-x-auto" data-testid="recent-scans" style={{ padding: 0 }}>
              <div className="px-6 py-4 border-b border-soft flex items-center justify-between">
                <div className="form-label mb-0" style={{ color: "var(--color-accent)" }}>Letzte Scans</div>
                <span className="body-sm">{stats.recent?.length || 0}</span>
              </div>
              {!isLoading && stats.recent?.length === 0 ? (
                <div className="p-8 body-md text-center">Noch keine Scans. Sobald echte Nutzer die Seite besuchen, tauchen sie hier auf.</div>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[11px] tracking-widest text-muted uppercase border-b border-soft">
                      <th className="px-6 py-3 font-medium">Page</th>
                      <th className="px-6 py-3 font-medium">Zeit</th>
                      <th className="px-6 py-3 font-medium">Gerät</th>
                      <th className="px-6 py-3 font-medium">Quelle</th>
                      <th className="px-6 py-3 font-medium">Lang</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent?.map((r) => (
                      <tr key={r.id} className="border-b border-soft last:border-0 hover:bg-olive-faint">
                        <td className="px-6 py-3 text-sm text-primary">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full mr-2 ${r.page === "partner" ? "bg-accent" : "bg-primary"}`}></span>
                          {r.page}
                        </td>
                        <td className="px-6 py-3 text-xs text-muted">{r.timestamp?.replace("T", " ").slice(0, 19)}</td>
                        <td className="px-6 py-3 text-xs">{r.device_type}</td>
                        <td className="px-6 py-3 text-xs">{r.utm_source || "direct"}</td>
                        <td className="px-6 py-3 text-xs uppercase">{r.language}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* TAB: LEAD PIPELINE */}
        {activeTab === "leads" && (
          <div data-testid="admin-tab-content-leads">
            <LeadPipelineKanban />
          </div>
        )}

        {/* TAB: GEO HEATMAP */}
        {activeTab === "geo" && (
          <div data-testid="admin-tab-content-geo">
            <GeoHeatmap />
          </div>
        )}

        {/* TAB: WAITLIST EXPORT */}
        {activeTab === "export" && (
          <div data-testid="admin-tab-content-export">
            <WaitlistExport />
          </div>
        )}
      </div>
    </div>
  );
}
